// Report UI language (user request 2026-09-08): the business's own country
// decides it (see server.py's get_public_report -> `lang`), not the
// visitor's browser — the recipient is the business owner. Scope is
// deliberately limited to this static report CHROME (headings, labels,
// buttons, empty states): finding content itself (audit/i18n_de.py's
// translated titles/evidence, the wide-report narrative) stays German
// regardless of `lang`, since the underlying legal checks stay
// Germany-law-specific for now — translating those is a separate, larger
// decision the user did not make here.
export const T = {
  de: {
    tabs: { overview: "Übersicht", visibility: "Sichtbarkeit", reputation: "Reputation", legal: "Recht & Struktur", plan: "Aktionsplan", consulting: "Support" },
    downloadPdf: "Als PDF speichern",
    pillars: {
      web: "Website & Technik", onpage: "On-Page-SEO", local: "Local SEO",
      gbp: "Google-Unternehmensprofil", reviews: "Bewertungen", ai: "KI-Sichtbarkeit",
    },
    hits: { detay: "Detail", marka: "Marke", sehir: "Ort", hizmet: "Leistung" },
    tone: { positive: "empfehlend", neutral: "neutral erwähnt", negative: "negativ" },
    // "all": the FindingsList severity-filter row's "show everything" button —
    // added to this same lookup rather than a new copy structure, per the
    // task note that severity labels already live here.
    severity: { critical: "Kritisch", high: "Hoch", medium: "Mittel", low: "Niedrig", positive: "Positiv", all: "Alle" },
    scoreBands: [
      { max: 39, emoji: "🔴", label: "Kritische digitale Schwäche" },
      { max: 59, emoji: "🟠", label: "Unter Marktstandard" },
      { max: 74, emoji: "🟡", label: "Funktional, aber ausbaufähig" },
      { max: 89, emoji: "🟢", label: "Starke digitale Präsenz" },
      { max: 100, emoji: "🔵", label: "Digitaler Marktführer" },
    ],
    cwv: { good: "Gut", ok: "Verbesserungswürdig", poor: "Schlecht" },
    daysAgo: (days) => days == null ? "–" : days === 0 ? "heute" : days === 1 ? "vor 1 Tag" : `vor ${days} Tagen`,
    checklistTeaser: (label, passed, total) => {
      if (!total) return `${label}: Es konnte keine funktionierende Website geprüft werden — das ist aktuell der größte Hebel für Ihre digitale Sichtbarkeit.`;
      const gap = total - passed;
      if (gap === 0) return `${label}: Sie erfüllen bereits alle ${total} geprüften Kriterien — stark!`;
      const ratio = passed / total;
      if (ratio >= 0.67) {
        return gap === 1
          ? `${label}: Von ${total} geprüften Kriterien erfüllen Sie nur ${passed}. Der eine verbleibende Punkt lohnt sich noch ein Blick.`
          : `${label}: Von ${total} geprüften Kriterien erfüllen Sie nur ${passed}. Die restlichen ${gap} lohnen sich noch einen Blick.`;
      }
      const urgency = ratio < 0.34 ? "sollten Sie dringend angehen" : "sollten Sie zeitnah angehen";
      return `${label}: Von ${total} geprüften Kriterien erfüllen Sie nur ${passed}. Die restlichen ${gap} ${urgency}.`;
    },
    // Coverage of the 24-item manual GBP checklist (see audit/scoring.py
    // gbp_manual_progress) — a count only, never a verdict, shown under the
    // gbp pillar bar so an unreviewed profile doesn't read as a silent low
    // score.
    manualReviewed: (reviewed, total) =>
      `Der Wert oben zählt die automatischen Prüfungen — ${reviewed} von ${total} weiteren Punkten sind bislang manuell geprüft.`,
    // A measurement without a date is a claim, not a measurement — the free
    // report leads with both of these (server.py's `measured_at`).
    measuredOn: (date) => `Gemessen am ${date}.`,
    verifyPromise: "Jede Zahl in diesem Bericht können Sie selbst nachprüfen. Wir haben nichts geschätzt und nichts hinzugefügt.",

    // --- Locked findings inventory (free tier) -------------------------------
    // Replaces the abstract checklistTeaser ratio with the real, named,
    // severity-ranked list of this business's own findings. Titles are shown,
    // evidence and remedy are paid — see server.py's _findings_teaser.
    lockedTitle: (n) => (n === 1 ? "1 weiteren Punkt haben wir gefunden" : `${n} weitere Punkte haben wir gefunden`),
    lockedDesc: "Die Titel stehen hier offen. Was genau gemessen wurde, wo es auf Ihrer Seite steht und mit welchem Schritt es behoben ist, steht im ausführlichen Bericht.",
    lockedItemHint: "Beweis und Lösungsschritt im Bericht",
    showAllPoints: (n) => `Alle ${n} Punkte anzeigen`,
    showFewerPoints: "Weniger anzeigen",

    // --- One finding handed over in full, as proof of value ------------------
    sampleTitle: "Ein Punkt komplett aufgeschlüsselt",
    sampleDesc: (n) => `So sieht im ausführlichen Bericht jeder einzelne der ${n} Punkte aus: Befund, Beweis, und der Schritt, der ihn behebt.`,
    sampleEvidenceLabel: "Was wir gemessen haben",
    sampleFixLabel: "Was zu tun ist",
    sampleEffortLabel: "Aufwand",
    sampleEffort: { low: "unter 1 Stunde", medium: "1–3 Stunden", high: "halber Tag oder mehr" },
    sampleFooter: "Diesen Punkt können Sie ab sofort selbst umsetzen — er kostet Sie nichts und zeigt Ihnen, wie der Rest aussieht.",

    reportNotFoundTitle: "Bericht nicht gefunden",
    reportNotFoundBody: "Dieser Link ist ungültig oder abgelaufen.",
    interestSent: "Anfrage gesendet — wir melden uns bei Ihnen.",
    interestFailed: "Anfrage konnte nicht gesendet werden.",
    you: "Sie",
    foundHeading: "Was wir konkret gefunden haben",
    passedTitle: "Was Sie bereits gut machen",
    passedDesc: "Diese Prüfpunkte haben wir gemessen — und Ihre Seite besteht sie.",
    passedAll: "Alles bestanden",
    noDataYet: "Für diesen Bereich liegen noch keine Daten vor.",
    scoreLabel: "Digitale Sichtbarkeit von 100",
    benchmarkLine: (median) => <>Vergleichbare Unternehmen in Ihrer Region liegen im Median bei{" "}<strong>{median}/100</strong>.</>,
    // Short caption next to the ScoreBenchmarkBar marker — same wording as benchmarkLine, just short.
    benchmarkCaption: "Regionaler Median",
    // Purely informational — never scored, never counted in gbp_manual_progress.
    // These fields only live in the owner's own GBP dashboard, and Google has
    // been showing fewer of them on the public profile/Maps listing, so we
    // can no longer reliably verify them ourselves either (owner request,
    // 2026-09-30: "google artık çoğu başlığı göstermiyor... bunları siz
    // kontrol etmelisiniz"). Grouped the same way as MANUAL_GBP in
    // audit/scoring.py (About/Services/Products/Posts) — same substance,
    // just addressed directly to the business instead of gated behind an
    // admin review.
    gbpSelfCheck: {
      title: "Nur Sie können das prüfen: Ihr Google-Unternehmensprofil",
      intro: "Für die folgenden Punkte haben wir keinen Zugriff auf Ihr Google-Unternehmensprofil-Dashboard — wir sehen nur, was öffentlich sichtbar ist, und Google zeigt einen wachsenden Teil davon dort nicht mehr zuverlässig an. Bitte prüfen Sie diese Punkte direkt in Ihrem eigenen Profil. Ein vollständig gepflegtes Profil ist ein wichtiger Teil Ihrer Sichtbarkeit bei Google.",
      groups: [
        {
          heading: "Über Ihr Unternehmen",
          items: [
            { title: "Ist ein Beschreibungstext ausgefüllt?", why: "Einer der wenigen Orte, an denen Sie in eigenen Worten erklären können, was Sie anbieten — Google und Kund*innen nutzen ihn zur Einordnung." },
            { title: "Werden darin Ihre Leistungen genannt?", why: "Fehlt der Bezug zu Ihren Leistungen, fällt es Google schwerer, Sie zu passenden Suchen zuzuordnen." },
            { title: "Wird Ihr Standort bzw. Einzugsgebiet genannt?", why: "Hilft Google, Sie bei lokalen Suchen richtig einzuordnen." },
            { title: "Wird ein Alleinstellungsmerkmal genannt?", why: "Hebt Sie gegenüber Wettbewerbern mit ähnlichem Angebot ab." },
            { title: "Ist der Text natürlich formuliert, ohne aneinandergereihte Suchbegriffe?", why: "Google bewertet künstlich wirkende Keyword-Häufungen negativ und kann das Profil dafür abstrafen." },
          ],
        },
        {
          heading: "Leistungen",
          items: [
            { title: "Sind im Bereich „Leistungen“ überhaupt Einträge vorhanden?", why: "Ohne Einträge wissen weder Google noch Kund*innen auf einen Blick, was Sie konkret tun." },
            { title: "Ist die Liste vollständig?", why: "Eine fehlende Leistung bedeutet: für genau diese Suche werden Sie nicht gefunden." },
            { title: "Hat jede Leistung eine kurze Beschreibung?", why: "Gibt Google zusätzlichen Kontext und wirkt auf Kund*innen vertrauenswürdiger als ein bloßes Stichwort." },
            { title: "Stimmen die gelisteten Leistungen mit Ihrer Website überein?", why: "Widersprüche zwischen Profil und Website wirken unseriös und schwächen Googles Vertrauen in Ihre Angaben." },
          ],
        },
        {
          heading: "Produkte",
          items: [
            { title: "Sind, falls zutreffend, Produkte im Profil hinterlegt?", why: "Produkte erscheinen direkt in der Google-Suche und in Maps — eine zusätzliche, kostenlose Fläche, um gefunden zu werden." },
          ],
        },
        {
          heading: "Beiträge & Aktualität",
          items: [
            { title: "Veröffentlichen Sie regelmäßig Beiträge (Angebote, Neuigkeiten, Fotos)?", why: "Signalisiert Google ein aktiv gepflegtes Profil und erscheint Kund*innen direkt im Suchergebnis." },
            { title: "Wurde das Profil in letzter Zeit überhaupt aktualisiert?", why: "Ein lange unverändertes Profil wirkt auf Kund*innen wie auf Google weniger vertrauenswürdig als ein aktiv gepflegtes." },
          ],
        },
      ],
    },
    comparisonTitle: "Ihre Position im Vergleich",
    comparisonDesc: "Unternehmen, die bei Google Maps und in der KI-Suche (ChatGPT, Perplexity) oben stehen, werden von Kunden oft automatisch gefunden — fast ohne aktive Werbung. Wer weiter unten steht, wird häufig gar nicht erst in Betracht gezogen.",
    yourValue: "Ihr Wert",
    gapLabel: "ABSTAND",
    targetBelow: (target) => <>Um von Kunden ähnlich mühelos gefunden zu werden wie die sichtbarsten Unternehmen in Ihrer Region, wäre ein Wert von mindestens{" "}<strong>{target}/100</strong> nötig.</>,
    targetMet: "Ihr Wert liegt bereits auf dem Niveau der sichtbarsten Unternehmen in Ihrer Region.",
    pillarTableArea: "Bereich",
    legalAlert: (n, hasImageFlag) => (
      <>
        <strong>{n} {n === 1 ? "rechtlicher Punkt" : "rechtliche Punkte"}</strong>{" "}
        bei der automatischen Prüfung aufgefallen
        {hasImageFlag && " — darunter ein Hinweis auf möglicherweise nicht lizenzierte Bilder"}.
      </>
    ),
    details: "Details",
    planEmptyState: "Der priorisierte 3-Monats-Plan ist Teil des ausführlichen Berichts — fordern Sie ihn unten an.",
    additionalNotesTitle: "Zusätzlich empfohlen",
    additionalNotesDesc: "Diese Punkte gelten unabhängig vom Befund für jedes Unternehmen und wirken sich nicht unmittelbar auf das Ranking aus — meist reine Mess- oder Komfortfunktionen.",
    // Comprehensive, cross-pillar guidance for a business's largest structural
    // gaps (e.g. no website at all) — deliberately separate from the 3-month
    // action items above, which stay tied to individual findings (owner
    // request 2026-10-01: a full recommendation "im Lichte aller geprüften
    // Bereiche", not just the pillar the gap was found in).
    comprehensiveGuidanceTitle: "Umfassende Empfehlungen für Ihre größten Lücken",
    comprehensiveGuidanceDesc: "Diese Empfehlungen berücksichtigen alle von uns geprüften Bereiche, nicht nur den, in dem die Lücke aufgefallen ist.",
    // Background/context reading appended under the personalized plan above —
    // general strategy education, not a measurement of THIS business, so it
    // stays clearly separate (owner request 2026-09-30). German-market
    // specific (GDPR, deutsche Verzeichnisse) — DE only, no EN mirror, hence
    // no `lang` prop needed in PlanTab: its presence on T.de is the gate.
    // Every number/date below was verified against its cited source before
    // writing (SOCi's 2026 Local Visibility Index; Google Marketing Live
    // 2026; Search Engine Land) — same standard as the hero headline stats.
    strategyTitle: "Hintergrund: Wie sich Werbung für lokale Dienstleister gerade verändert",
    strategyIntro: "Es gibt nicht mehr nur einen Weg, einen lokalen Dienstleister zu finden. Manche suchen bei Google, andere navigieren über die Karte, und ein wachsender Teil fragt einen Assistenten wie ChatGPT: „Empfiehl mir einen guten Fliesenleger in Essen.“ Eine zeitgemäße Strategie deckt alle drei Wege gleichzeitig ab.",
    strategySections: [
      {
        heading: "1. Grundlage: Google-Unternehmensprofil und Bewertungen",
        paragraphs: [
          "Alles beginnt hier. Sowohl die Kartenergebnisse als auch die Empfehlungen der KI-Assistenten stützen sich zu großen Teilen auf das Unternehmensprofil und die Bewertungen.",
        ],
        items: [
          "Kategorien, Leistungen, Einzugsgebiete und Öffnungszeiten vollständig pflegen.",
          "Wöchentlich echte Arbeitsfotos und kurze Beiträge veröffentlichen.",
          "Regelmäßig Bewertungen sammeln und jede davon persönlich auf Deutsch beantworten — deutsche Kund*innen achten besonders auf Bewertungen.",
        ],
      },
      {
        heading: "2. ChatGPT und KI-Sichtbarkeit",
        paragraphs: [
          "Die Chance ist groß, aber bisher nutzen sie wenige Betriebe. Laut SOCis „2026 Local Visibility Index“ (über 350.000 untersuchte Standorte) empfehlen KI-Plattformen aktuell nur rund 1,2 % der Standorte auf ChatGPT, während die Sichtbarkeit im lokalen 3er-Paket von Google bei 35,9 % liegt — wer hier früh ansetzt, hat einen deutlichen Vorsprung.",
        ],
        items: [
          "Bing nicht vernachlässigen: ChatGPT stützt sich laut Recherchen von Search Engine Land bei Websuchen auf den Bing-Index und wertet die Ergebnisse mit eigener Logik aus. Ein Bing-Places-Profil anlegen und mit dem Google-Profil konsistent halten.",
          "Deutsche Verzeichnisse: Ein einheitlicher Name-Adresse-Telefon-Eintrag auf Plattformen wie Gelbe Seiten, Das Örtliche, 11880 oder ProvenExpert stärkt das Vertrauen der KI in den Betrieb.",
          "Frage-Antwort-Inhalte: Klare Preisspannen, Einzugsgebiete und häufige Fragen auf der Website — KI-Systeme überspringen vage Angaben und empfehlen eher, was eindeutig ist.",
          "Monatlicher Test: Einmal im Monat 10–15 branchenrelevante Fragen an ChatGPT stellen und verfolgen, ob der eigene Betrieb genannt wird.",
        ],
      },
      {
        heading: "3. Demand Gen: neue Anzeigenflächen",
        paragraphs: [
          "Demand Gen ermöglicht es, ins Blickfeld zu kommen, bevor aktiv gesucht wird. Über Kanalsteuerungen lässt sich festlegen, wo Anzeigen erscheinen — auf YouTube, in Gmail, bei Discover, in Google Maps und im Displaynetzwerk. Maps ist dabei neu: Seit Mai 2026 (verkündet auf der Google Marketing Live 2026) lassen sich Demand-Gen-Anzeigen auch dort schalten.",
        ],
        items: [
          "Discover und YouTube Shorts: Vorher-Nachher-Aufnahmen, 15-Sekunden-Einblicke in den Arbeitsalltag — das ist der Inhalt, der bei lokalen Dienstleistungen am meisten Interesse weckt.",
          "Maps: Vor der Schaltung prüfen — Maps liefert laut Google nur aus, wenn im Konto eine verknüpfte Standorterweiterung aktiv ist.",
          "Displaynetzwerk zunächst deaktiviert lassen, damit das Budget nicht unkontrolliert abfließt — zuerst auf YouTube und Discover Ergebnisse sammeln.",
          "Ausrichtung: Standort im Einzugsgebiet, Besucher*innen von Konkurrenz- und Branchenseiten, passende Sucheingaben sowie Remarketing auf Website-Besucher*innen.",
        ],
        note: "Demand Gen ersetzt die Suche nicht, sondern nährt sie: Wer den Betrieb zuerst bei Discover sieht, kennt die Marke schon, wenn er zwei Wochen später gezielt sucht.",
      },
      {
        heading: "4. Suchanzeigen: weiterhin das Zentrum des Verkaufs",
        paragraphs: [
          "Bei akutem, klar formuliertem Bedarf („Fliesenleger Essen“, „Heizung Notdienst“) bleiben Suchanzeigen der Kanal mit der schnellsten Reaktion. Demand Gen erzeugt Nachfrage, die Suchanzeige fängt sie auf.",
        ],
      },
      {
        heading: "5. Besonderheit in Deutschland: Einwilligung und Messung",
        paragraphs: [
          "Ohne Cookie-Einwilligung bleibt die Messung lückenhaft (DSGVO). Ohne korrekt eingerichteten Consent Mode v2 lässt sich keine Kampagne zuverlässig auswerten. Zusätzlich lohnt sich in jedem Formular und am Telefon die Frage „Wie haben Sie uns gefunden?“ — eine einfache Frage, die verlässlich zeigt, welchen Anteil Google, Karte, KI-Assistent und Weiterempfehlung tatsächlich haben.",
        ],
      },
    ],
    strategyBudgetTitle: "Empfohlene Budgetverteilung",
    strategyBudgetItems: [
      { pct: 40, label: "Google-Suchanzeigen" },
      { pct: 30, label: "Demand Gen (YouTube, Discover, Maps)" },
      { pct: 15, label: "Remarketing" },
      { pct: 15, label: "Profil-, Bewertungs- und KI-Sichtbarkeitsarbeit" },
    ],
    strategyConclusion: "In den kommenden Jahren gewinnt nicht der Betrieb mit dem größten Werbebudget, sondern der, der auf jedem Weg des Kunden konsequent sichtbar ist: ein starkes Profil auf der Karte, eindeutige Angaben, denen die KI vertraut, und eine Bekanntheit, die Demand Gen bereits vor der aktiven Suche aufgebaut hat.",
    strategySource: "Quellen: SOCi „2026 Local Visibility Index“; Google Marketing Live 2026 (Mai 2026); Search Engine Land.",
    strategyToggle: "Hintergrund zur Anzeigenstrategie anzeigen",
    aiVisibilityDesc: (mentioned, total) => <><strong>{mentioned} von {total}</strong>{" "}realistischen Kundenanfragen nennen Ihr Unternehmen namentlich.</>,
    aiVisibilityUnavailable: "Test derzeit nicht möglich (API-Fehler beim Anbieter) — kein Ergebnis, nicht „0 % Sichtbarkeit\".",
    claudeTestTitle: "Claude-Sichtbarkeitstest",
    chatgptTestTitle: "ChatGPT-Sichtbarkeitstest",
    geminiTestTitle: "Gemini-Sichtbarkeitstest",
    perplexityTestTitle: "Perplexity-Sichtbarkeitstest",
    googleAiOverviewTitle: "Google-KI-Übersicht (AI Overview)",
    googleAiModeTitle: "Google-KI-Modus (AI Mode)",
    aiVisibilityGroupTitle: "KI-Sichtbarkeit",
    aiActionSummaryTitle: "So erscheinen Sie in diesen KI-Antworten",
    citedShareDesc: (cited, total) => <><strong>{cited} von {total}</strong>{" "}verlinken dabei auch Ihre Website als Quelle.</>,
    showAnswer: "Antwort der KI anzeigen",
    hideAnswer: "Antwort verbergen",
    sourceAttributionTitle: "Woher die KI ihre Informationen bezieht",
    sourceAttributionDesc: "Diese Quellen wurden von der KI in den obigen Antworten tatsächlich zitiert — geordnet danach, wie oft sie vorkamen. Orange markierte Einträge sind Verzeichnisse, in denen Sie noch nicht eingetragen sind.",
    sourceAttributionCount: (n) => `${n}× zitiert`,
    sourceAttributionRegister: "Jetzt eintragen",
    consistencyLabel: "Konsistenz",
    consistencyRate: (mentioned, total, rate) => `${mentioned} von ${total} Versuchen (${rate ?? 0}%)`,
    aiVolumeTitle: "Geschätztes KI-Nachfragesignal",
    aiVolumeDesc: "Wie oft diese Fragen geschätzt in KI-Suchen vorkommen, abgeleitet aus Google-Suchdaten — keine echten ChatGPT-/Gemini-Nutzungszahlen, sondern ein Näherungswert.",
    aiVolumePerMonth: (n) => `${n}/Monat`,
    competitorAiTitle: "Sie vs. Ihre stärksten Wettbewerber in KI-Antworten",
    competitorAiDesc: (n) => `Wie oft jeder in den ${n} oben getesteten KI-Antworten namentlich vorkommt.`,
    externalDomainsTitle: "Weitere von der KI genannte Anbieter",
    externalDomainsDesc: "Diese Websites wurden in den KI-Antworten als Quelle genannt, gehören aber keinem der oben erfassten Unternehmen — vermutlich weitere Wettbewerber, die nicht Teil dieser Suche waren.",
    questionsTitle: "Fragen Ihrer Kunden",
    questionsDesc: (seed, covered, total) => (
      <>
        Das fragen Menschen bei Google rund um „{seed}". Auf Ihrer Website finden sich sichtbare
        Inhalte zu <strong>{covered} von {total}</strong> dieser Fragen. Jede unbeantwortete Frage
        ist ein fertiges Thema für eine Unterseite oder einen FAQ-Eintrag — und genau solche
        Frage-Antwort-Abschnitte werden von KI-Assistenten überdurchschnittlich häufig zitiert.
      </>
    ),
    localVariantsTitle: "Lokale Suchvarianten",
    localVariantsDesc: (covered, total) => (
      <>
        So wird in Ihrer Region konkret gesucht — meist mit Stadtteil oder Zusatzleistung. Ihre
        Website deckt davon <strong>{covered} von {total}</strong> ab. Für jeden fehlenden
        Stadtteil gibt es aktuell keine Seite, die dort ranken könnte.
      </>
    ),
    questionsSource: "Quelle: Google-Suchvorschläge. „Nicht gefunden“ heißt, dass wir auf den gelesenen Seiten keine passenden Inhalte gefunden haben — nicht zwingend, dass die Frage nirgends beantwortet wird.",
    mapsTitle: "Ihre Position bei Google Maps",
    mapsDesc: (kw) => <>Live-Ergebnis für die Suche „{kw}" — so sehen Sie es gerade bei Google Maps.</>,
    mapsShortLabel: "Google Maps",
    mapsActionSummaryTitle: "So verbessern Sie Ihre Sichtbarkeit bei Google Maps",
    organicTitle: "Ihre Position in der Google-Suche",
    organicShortLabel: "Google-Suche",
    rankFound: (rank, total) => <>Sie stehen auf Platz <strong>{rank}</strong> von{" "}{total} sichtbaren Ergebnissen.</>,
    mapsNotFound: (total) => `Sie erscheinen nicht unter den ersten ${total} Ergebnissen bei Google Maps für diese Suche.`,
    organicNotFound: (total) => `Sie erscheinen nicht unter den ersten ${total} organischen Ergebnissen bei Google für diese Suche.`,
    heatmapTitle: "Sichtbarkeits-Heatmap",
    // Rewritten for a non-expert reader (owner feedback, 2026-09-30: "bu rapora
    // uzman birisi bakmayacak" — leads with the result in plain language
    // instead of the measurement method first).
    heatmapDesc: (size, kw) => `Wir haben für Sie gemessen, wo Sie bei Google Maps auftauchen — an ${size * size} Punkten rund um Ihre Region, jeweils für die Suche „${kw}". Grün heißt: Sie stehen ganz oben. Rot heißt: Sie erscheinen dort gar nicht.`,
    technicalGroupTitle: "Technik & Off-Page-Signale",
    citationsTitle: "Einträge in Verzeichnissen & sozialen Medien",
    citationsDescDetailed: (found, total) => <>Konsistente Einträge auf großen Verzeichnis-Websites ("Citations") und aktive Profile in sozialen Medien sind wichtige Signale für lokales Google-Ranking. Sie sind auf <strong>{found} von {total}</strong> geprüften Plattformen auffindbar.{" "}Geprüft wird, ob ein Eintrag existiert — nicht, ob die hinterlegten Kontaktdaten dort korrekt sind.</>,
    citationsDescTeaser: (found, total) => <>Konsistente Einträge auf großen Verzeichnis-Websites ("Citations") und aktive Profile in sozialen Medien sind wichtige Signale für lokales Google-Ranking. Sie sind auf <strong>{found} von {total}</strong> geprüften Plattformen auffindbar.{" "}Welche Plattformen das genau sind — und wo noch Einträge fehlen — sehen Sie im ausführlichen Bericht.</>,
    directories: "Branchenverzeichnisse",
    socialMedia: "Soziale Medien",
    morePlatforms: "Weitere Plattformen (nicht automatisch prüfbar)",
    signUpAt: (label) => `Bei ${label} eintragen`,
    backlinksTitle: "Verweisende Domains",
    backlinksDesc: (domains, total) => <><strong>{domains}</strong> unterschiedliche Websites verlinken auf Ihre Seite ({total} Backlinks insgesamt). Das ist ein Signal für die Autorität Ihrer Website außerhalb von Google — kein Besucherzähler.</>,
    pagespeedTitle: "Ladezeit & technische Qualität (Mobil)",
    pagespeedDesc: "Gemessen mit Google PageSpeed Insights — dieselbe Technik, die auch Googles Ranking beeinflusst.",
    pagespeedMetrics: ["Performance", "SEO", "Barrierefreiheit", "Best Practices"],
    lcpLabel: "Largest Contentful Paint (Ladezeit größtes Element)",
    clsLabel: "Cumulative Layout Shift (Verschieben beim Laden)",
    analyticsTitle: "Analyse-Tools",
    analyticsDesc: "Wissen Sie, wie viele Besucher oder Anfragen Ihre Website tatsächlich erzeugt?",
    analyticsNone: "Es wurde kein Analyse-Tool (z. B. Google Analytics) auf Ihrer Website gefunden — aktuell können Sie nicht nachvollziehen, wie viele Besucher Ihre Seite hat.",
    techSeoTitle: "Technische Auffindbarkeit",
    techSeoDesc: "Kann Google Ihre Startseite überhaupt in den Suchindex aufnehmen, und gibt es Seiten, die sich versehentlich gegenseitig duplizieren?",
    techSeoTeaser: (positive, issues) => (
      <>
        {positive > 0 && <><strong>{positive}</strong>{" "}{positive === 1 ? "Bestätigung" : "Bestätigungen"}{issues > 0 ? " und " : "."}</>}
        {issues > 0 && <><strong>{issues}</strong>{" "}{issues === 1 ? "Verbesserungspunkt" : "Verbesserungspunkte"}{" "}bei der Auffindbarkeit für Google. Details im ausführlichen Bericht.</>}
      </>
    ),
    // Ad conversion-tracking (owner request 2026-10-02): "google ads ve
    // meta'da reklam veriyor ... dönüşüm takibi yapılmıyor" — a Meta Pixel
    // or Google Ads tag found on the site strongly suggests ads run there;
    // flagged only when the base tag exists with no conversion event.
    adsTrackingTitle: "Werbe-Erfolgsmessung",
    adsTrackingDesc: "Wenn Sie Anzeigen bei Google oder Meta (Facebook/Instagram) schalten, zeigt sich hier, ob eine Anfrage oder ein Kauf als Erfolg auch tatsächlich gezählt wird.",
    adsTrackingTeaser: (n) => (
      <>Bei der automatischen Prüfung {n === 1 ? "ist 1 Punkt" : `sind ${n} Punkte`} zur Werbe-Erfolgsmessung aufgefallen. Details dazu finden Sie im ausführlichen Bericht.</>
    ),
    // Content opportunities (owner request 2026-10-07): concrete "write
    // this, post this" suggestions grounded in question_gaps/sitemap
    // topic-gap/missing-directory signals already shown elsewhere on this
    // tab — never a severity concept, hence "impact"/"effort", not
    // "critical"/"high"/"medium"/"low" finding severity.
    contentOpportunitiesTitle: "Content-Chancen",
    contentArticlesTitle: "Mögliche Artikel für Ihre Website",
    quickPostsTitle: "Was Sie heute teilen können",
    missingDirectoriesTitle: "Verzeichnisse, in denen Ihre Wettbewerber gelistet sind",
    impactLabel: (level) => ({ high: "Hohe Wirkung", medium: "Mittlere Wirkung", low: "Geringe Wirkung" }[level] || level),
    effortLabel: (level) => ({ high: "Hoher Aufwand", medium: "Mittlerer Aufwand", low: "Geringer Aufwand" }[level] || level),
    missingDirectoryFoundBy: (names) => `Gefunden bei: ${names.join(", ")}`,
    napTitle: "NAP-Konsistenz",
    napDesc: "Stimmen Name, Adresse und Telefonnummer über alle Plattformen hinweg überein? Uneinheitliche Angaben schwächen das Vertrauen, das Google in Ihre Standortdaten setzt.",
    napSummary: (consistent, checked, hasDetail) => (
      <><strong>{consistent} von {checked}</strong> geprüften Plattformen stimmen vollständig überein.{" "}{hasDetail ? "Details zu den abweichenden Einträgen finden Sie unten." : "Welche Plattform genau abweicht, zeigen wir Ihnen im ausführlichen Bericht."}</>
    ),
    napStatus: { match: "stimmt überein", partial: "teilweise abweichend — bitte prüfen", mismatch: "abweichend — bitte prüfen" },
    legalTitle: "Rechtliche Prüfpunkte",
    legalTeaser: (n, hasImageFlag) => (
      <>Bei der automatischen Prüfung {n === 1 ? "ist 1 Punkt" : `sind ${n} Punkte`} aufgefallen, die Sie prüfen lassen sollten{hasImageFlag ? " — darunter ein Hinweis auf möglicherweise nicht lizenzierte Bilder auf Ihrer Website" : ""}. In Deutschland führen gerade diese Themen häufig zu Abmahnungen, die schnell mehrere hundert Euro kosten können. Details dazu finden Sie im ausführlichen Bericht.</>
    ),
    // Checklist framing for the paid report's legal section (owner request
    // 2026-10-01: "web sitenizi inceledik ... checklistini paylaşıyoruz" —
    // every category always shown, green when clean, red with a fix hint
    // when not — replaces the old "only show something when there IS an
    // issue" flat list (legalFullIntro, removed).
    legalChecklistTitle: "Rechtliche Checkliste Ihrer Website",
    legalChecklistIntro: "Wir haben Ihre Website untersucht und teilen hier eine Checkliste der rechtlichen Punkte, die eine Website in Deutschland haben sollte. Was wir gefunden haben, ist grün markiert — was fehlt oder zu prüfen ist, rot, jeweils mit einem kurzen Hinweis, was zu tun ist.",
    legalChecklist: {
      impressum: "Impressum",
      impressumOk: "Ein Impressum wurde gefunden und enthält die wichtigsten Pflichtangaben.",
      datenschutz: "Datenschutzerklärung",
      datenschutzOk: "Eine Datenschutzerklärung wurde gefunden und deckt die wichtigsten Punkte ab.",
      consent: "Cookie- und Tracking-Einwilligung",
      consentOk: "Keine Hinweise auf Tracking ohne vorherige Einwilligung gefunden.",
      images: "Bildrechte",
      imagesOk: "Keine Hinweise auf nicht lizenzierte Bilder gefunden.",
      agb: "AGB & Widerrufsbelehrung",
      agbOk: "AGB wurden gefunden.",
      ownSite: "Eigene Website",
      other: "Weitere rechtliche Hinweise",
    },
    legalDisclaimer: (
      <><strong>Dies ist keine Rechtsberatung.</strong> Geprüft wurde ausschließlich, was auf Ihrer Website technisch sichtbar ist. Ob und in welchem Umfang eine Pflicht für Sie gilt, hängt von Ihrem Einzelfall ab — bitte lassen Sie die Punkte im Zweifel anwaltlich prüfen.</>
    ),
    linkStructureTitle: "Link-Struktur",
    linkStructureTeaser: (positive, issues) => (
      <>
        {positive > 0 && <><strong>{positive}</strong> positive{" "}{positive === 1 ? "Bestätigung" : "Bestätigungen"}{issues > 0 ? " und " : "."}</>}
        {issues > 0 && <><strong>{issues}</strong>{" "}{issues === 1 ? "Verbesserungspunkt" : "Verbesserungspunkte"}{" "}bei der internen Verlinkung (Hauptmenü, interne Links). Details im ausführlichen Bericht.</>}
      </>
    ),
    linkStructureDesc: "Ist jede wichtige Seite über das Hauptmenü erreichbar, und funktionieren die internen Links auf Ihrer Website?",
    reviewsTitle: "Google-Bewertungen",
    reviewsLabel: "Bewertungen",
    reviewQualityLabel: "Bewertungsqualität",
    averageRatingLabel: "Durchschnitt",
    newIn90Days: "neu in 90 Tagen",
    newestReview: "Neueste Bewertung",
    showcaseQualityLabel: "Vitrin-Bewertungsqualität",
    showcaseQualityNote: "Wie aussagekräftig die von Google standardmäßig gezeigten Bewertungen sind (Ort, Leistung, Details) — kein Notenschnitt, daher auf einer 100er- statt 5er-Skala.",
    // Replaced the previous `reviewsBait` (2026-09-20). That line — "Mit unserer
    // neuen Google-Bewertungsstrategie verschwenden Sie keine Zeit mehr mit
    // Bewertungen, die das Ranking gar nicht beeinflussen!" — was the only
    // sentence in the whole product that broke its own rule: an unverifiable
    // superlative with an exclamation mark, inside a document whose premise is
    // that every claim is checkable in seconds. Same curiosity hook, but every
    // number below comes from real data (review_count, benchmark.review_median,
    // review_analysis.index) and nothing is asserted that isn't measured.
    // Split into a short intro sentence (measured context) + a bulleted list
    // of the paid-report deliverables (reviewsDeliverables) — the previous
    // version crammed all of it into one four-clause run-on sentence
    // separated by semicolons, which the owner flagged as hard to read
    // (2026-09-30: "madde madde... nokta başında"). Every claim still ties to
    // a real feature: reputation_service.py caps negative_themes/
    // positive_themes at 4 each (not 5 — verified before writing this),
    // appealable_reviews/respondable_reviews already generate the
    // Google-report path and the ready-to-post reply draft, and the
    // "reviews_no_locality" finding is the real basis for the Maps-visibility
    // bullet — phrased as a contributing factor, never a ranking promise,
    // since the AGB explicitly rules out promising a specific placement.
    reviewsTeaser: (count, median, showcaseIndex) => (
      <>
        Sie haben <strong>{count} Bewertungen</strong>
        {median != null && <> — der Median vergleichbarer Betriebe in Ihrer Region liegt bei <strong>{median}</strong></>}.
        Für das Ranking zählt aber nicht nur die Anzahl: wie aktuell die Bewertungen sind,
        ob Sie darauf antworten und wie konkret sie geschrieben sind, wirken mit.
        {showcaseIndex != null && <> Ihre von Google hervorgehobenen Bewertungen erreichen dabei <strong>{showcaseIndex}/100</strong>.</>}
      </>
    ),
    reviewsDeliverablesLead: "Im ausführlichen Bericht sehen Sie:",
    reviewsDeliverables: [
      "Bis zu vier Themen, die Kund*innen am häufigsten loben — und bis zu vier, die sie am häufigsten kritisieren",
      "Zu jeder kritischen Bewertung eine fertige Antwortvorlage — oder, bei einem Verstoß gegen Googles Richtlinien, den direkten Weg zur Meldung bei Google",
      "Wie der Inhalt neuer Bewertungen Ihre Sichtbarkeit bei Google Maps unterstützen kann",
    ],
    reviewTipsTitle: "Tipps zum Bewertungsmanagement",
    reviewTipsRequestTitle: "Vorlagen zum Bewertungen einholen",
    reviewTipsWhatsapp: "WhatsApp / SMS:",
    reviewTipsWhatsappText: (name) => `„Hallo! Wenn Sie mit ${name} zufrieden waren, würden wir uns sehr über eine kurze Google-Bewertung freuen — dauert nur 1 Minute. [Google-Bewertungslink]"`,
    reviewTipsEmail: "E-Mail:",
    reviewTipsEmailText: "„Vielen Dank, dass Sie mit uns zusammengearbeitet haben. Ihre Erfahrung ist uns wichtig. Dürfen wir Sie um eine kurze Google-Bewertung bitten? Das dauert nur 1 Minute. [Google-Bewertungslink]\"",
    reviewTipsNegativeTitle: "Reaktion auf negative Bewertungen (3 Schritte)",
    reviewTipsNegativeStep1: "Bedanken:",
    reviewTipsNegativeStep1Text: "„Vielen Dank für Ihr Feedback. Kundenzufriedenheit ist uns sehr wichtig.\"",
    reviewTipsNegativeStep2: "Empathie zeigen:",
    reviewTipsNegativeStep2Text: "„Es tut uns wirklich leid, das zu hören. Das entspricht nicht unserem Anspruch.\"",
    reviewTipsNegativeStep3: "Lösung anbieten:",
    reviewTipsNegativeStep3Text: "„Wir möchten das direkt mit Ihnen klären. Bitte kontaktieren Sie uns unter [Telefon/E-Mail], damit wir das in Ordnung bringen können.\"",
    reviewTipsFooter: "Tipp: Antworten Sie innerhalb von 24 Stunden auf negative Bewertungen — eine schnelle, professionelle Reaktion schafft Vertrauen bei anderen Kunden.",
    showcaseTitle: "Ihre sichtbarsten Google-Bewertungen",
    showcaseDesc: "Google zeigt Nutzern standardmäßig nur eine kleine Auswahl der besten Bewertungen. Genau diese Auswahl sehen Sie hier — sie ist Ihr digitales Aushängeschild.",
    noSpecificDetails: "ohne konkrete Details",
    strengthLabel: (q) => `Aussagekraft ${q}/5`,
    strengthTooltip: "Wie viele lokale SEO-relevante Details diese Bewertung enthält (Ort, Marke, Leistung, Ausführlichkeit)",
    reputationTitle: "Online-Reputation im Detail",
    reputationDesc: (sample, total) => <>Analyse von {sample} der insgesamt {total}{" "}Google-Bewertungen — nicht nur der 5, die Google standardmäßig zeigt.</>,
    responseRate: "Antwortrate",
    negativeReviews: "Negative Bewertungen",
    responseQualityIntro: "Antworten auf Bewertungen wirken",
    responseQuality: { individuell: "individuell auf den jeweiligen Fall eingehend", standardisiert: "wie eine Standardvorlage", none: "nicht vorhanden — es wurde keine einzige Antwort gefunden" },
    positiveThemes: "Worüber Kunden sich freuen",
    negativeThemes: "Worüber Kunden sich beschweren",
    appealableTitle: "Bewertungen, die Sie bei Google melden könnten",
    appealableDesc: "Diese Bewertungen zeigen Anzeichen, die gegen Googles eigene Richtlinie für Bewertungsinhalte verstoßen könnten. Das ist keine Löschgarantie — Google entscheidet über jede Meldung einzeln.",
    appealableCategory: {
      off_topic: "Kein Bezug zum eigenen Besuch",
      spam_fake: "Kein echter Besuch erkennbar",
      conflict_of_interest: "Mögliche Befangenheit (z. B. Wettbewerber)",
      harassment_hate: "Beleidigung statt Kritik",
      restricted_content: "Unzulässiger Inhalt",
    },
    appealableViewOnGoogle: "Auf Google ansehen & melden",
    appealableNoneFound: (n) => `Wir haben Ihre ${n} negativen Bewertungen mit Text gegen Googles Richtlinie für Bewertungsinhalte geprüft — keine davon zeigt Anzeichen, die eine Meldung bei Google aussichtsreich machen würden.`,
    respondableTitle: "Antwortvorschlag für diese Bewertungen",
    respondableDesc: "Diese Bewertungen verstoßen nicht gegen Googles Richtlinie — hier ist ein fertiger Antwortentwurf, den Sie direkt öffentlich unter der jeweiligen Bewertung auf Google posten können.",
    respondableCopy: "Kopieren",
    respondableCopied: "In die Zwischenablage kopiert",
    respondableViewOnGoogle: "Auf Google ansehen",
    competitorTableTitle: "Im Vergleich zu Ihren stärksten Wettbewerbern",
    company: "Unternehmen",
    rating: "Rating",
    lastReview: "Letzte Bewertung",
    topNegativeTheme: "Häufigstes negatives Thema",
    youSuffix: (name) => `${name} (Sie)`,
    thankYou: "Danke — wir melden uns bei Ihnen.",
    ctaTitleDetailed: "Fragen zu diesem Bericht?",
    ctaDesc: "Kein Verkaufsgespräch — wenn Sie mehr wissen möchten, erreichen Sie mich gerne hier. Ganz unverbindlich.",
    formName: "Ihr Name (optional)",
    formContact: "Telefon oder E-Mail",
    formMessage: "Ihre Nachricht (optional)",
    sendMessage: "Nachricht senden",
    requestDetailed: "Ausführlichen Bericht anfordern",
    trustedBy: "Vertrauen von Unternehmen wie Ihrem",
    imgConfirmed: "Bestätigt:",
    imgFoundElsewhere: (n) => `Dieses Bild wurde auch auf ${n} anderen, unabhängigen Seiten gefunden —`,
    imgMore: (n) => `+${n} weitere`,
    imgNoMatches: "Keine Treffer auf anderen Seiten gefunden.",
    back: "Zurück",
    next: "Weiter",
    imagesCount: (n) => `${n} Bilder`,
    gbpPhotosTitle: "Bilder in Ihrem Google-Unternehmensprofil",
    gbpPhotosDesc: "Wir haben auch die von Ihnen selbst hochgeladenen Fotos in Ihrem Google-Unternehmensprofil per Bildrückwärtssuche geprüft — nicht nur die auf Ihrer Website.",
    gbpImagesCleanTitle: "Bilder in Ihrem Google-Unternehmensprofil geprüft",
    gbpImagesCleanDesc: (n) => `Wir haben ${n} von Ihnen selbst hochgeladene Fotos in Ihrem Google-Unternehmensprofil per Bildrückwärtssuche geprüft — keines davon wurde auf einer anderen, unabhängigen Seite gefunden.`,
  },
  en: {
    tabs: { overview: "Overview", visibility: "Visibility", reputation: "Reputation", legal: "Legal & Structure", plan: "Action Plan", consulting: "Support" },
    downloadPdf: "Save as PDF",
    pillars: {
      web: "Website & Technology", onpage: "On-Page SEO", local: "Local SEO",
      gbp: "Google Business Profile", reviews: "Reviews", ai: "AI Visibility",
    },
    hits: { detay: "Detail", marka: "Brand", sehir: "Location", hizmet: "Service" },
    tone: { positive: "recommending", neutral: "mentioned neutrally", negative: "negative" },
    severity: { critical: "Critical", high: "High", medium: "Medium", low: "Low", positive: "Positive", all: "All" },
    scoreBands: [
      { max: 39, emoji: "🔴", label: "Critical Digital Weakness" },
      { max: 59, emoji: "🟠", label: "Below Market Standard" },
      { max: 74, emoji: "🟡", label: "Functional but Underperforming" },
      { max: 89, emoji: "🟢", label: "Strong Digital Presence" },
      { max: 100, emoji: "🔵", label: "Digital Market Leader" },
    ],
    cwv: { good: "Good", ok: "Needs Improvement", poor: "Poor" },
    daysAgo: (days) => days == null ? "–" : days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`,
    checklistTeaser: (label, passed, total) => {
      if (!total) return `${label}: no working website could be checked — this is currently the biggest lever for your digital visibility.`;
      const gap = total - passed;
      if (gap === 0) return `${label}: you already meet all ${total} checked criteria — strong!`;
      const ratio = passed / total;
      if (ratio >= 0.67) {
        return `${label}: of ${total} checked criteria, you meet only ${passed}. The remaining ${gap} ${gap === 1 ? "is" : "are"} still worth a look.`;
      }
      const urgency = ratio < 0.34 ? "you should address this urgently" : "you should address this soon";
      return `${label}: of ${total} checked criteria, you meet only ${passed}. The remaining ${gap} ${urgency}.`;
    },
    manualReviewed: (reviewed, total) =>
      `The score above already counts the automated checks — ${reviewed} of ${total} further points have been manually reviewed so far.`,
    measuredOn: (date) => `Measured on ${date}.`,
    verifyPromise: "You can verify every number in this report yourself. Nothing here is estimated and nothing is invented.",

    lockedTitle: (n) => (n === 1 ? "We found 1 more point" : `We found ${n} more points`),
    lockedDesc: "The titles are all here. What exactly was measured, where it sits on your site, and the step that fixes it are in the detailed report.",
    lockedItemHint: "Evidence and fix in the report",
    showAllPoints: (n) => `Show all ${n} points`,
    showFewerPoints: "Show fewer",

    sampleTitle: "One point, worked out in full",
    sampleDesc: (n) => `This is what each of the ${n} points looks like in the detailed report: finding, evidence, and the step that fixes it.`,
    sampleEvidenceLabel: "What we measured",
    sampleFixLabel: "What to do",
    sampleEffortLabel: "Effort",
    sampleEffort: { low: "under 1 hour", medium: "1–3 hours", high: "half a day or more" },
    sampleFooter: "You can act on this one straight away — it costs you nothing and shows you what the rest looks like.",

    reportNotFoundTitle: "Report not found",
    reportNotFoundBody: "This link is invalid or has expired.",
    interestSent: "Request sent — we'll be in touch.",
    interestFailed: "Request could not be sent.",
    you: "You",
    foundHeading: "What we specifically found",
    passedTitle: "What you already do well",
    passedDesc: "We measured these checks — and your site passes them.",
    passedAll: "All passed",
    noDataYet: "No data is available for this section yet.",
    scoreLabel: "Digital Visibility out of 100",
    benchmarkLine: (median) => <>Comparable businesses in your region have a median of{" "}<strong>{median}/100</strong>.</>,
    benchmarkCaption: "Regional median",
    gbpSelfCheck: {
      title: "Only you can check this: your Google Business Profile",
      intro: "We have no access to your Google Business Profile dashboard for the points below — we only see what's publicly visible, and Google has been showing less and less of it there too. Please check these directly in your own profile. A fully maintained profile is an important part of your visibility on Google.",
      groups: [
        {
          heading: "About your business",
          items: [
            { title: "Is a description filled in?", why: "One of the few places where you explain what you offer in your own words — Google and customers both use it to understand your business." },
            { title: "Does it mention your actual services?", why: "Without that connection, it's harder for Google to match you to the right searches." },
            { title: "Does it mention your location or service area?", why: "Helps Google place you correctly in local searches." },
            { title: "Does it mention what sets you apart?", why: "Differentiates you from competitors offering something similar." },
            { title: "Does it read naturally, without a string of stuffed keywords?", why: "Google penalizes profiles that read as artificially keyword-stuffed." },
          ],
        },
        {
          heading: "Services",
          items: [
            { title: "Are there any entries at all under \"Services\"?", why: "Without entries, neither Google nor customers can tell at a glance what you actually do." },
            { title: "Is the list complete?", why: "A missing service means you won't be found for exactly that search." },
            { title: "Does each service have a short description?", why: "Gives Google more context and reads as more trustworthy to customers than a bare label." },
            { title: "Do the listed services match your website?", why: "A mismatch between profile and website looks unreliable and weakens Google's trust in your data." },
          ],
        },
        {
          heading: "Products",
          items: [
            { title: "Are products listed on the profile, where relevant?", why: "Products show up directly in Google Search and Maps — extra, free space to be found." },
          ],
        },
        {
          heading: "Posts & freshness",
          items: [
            { title: "Do you post regularly (offers, news, photos)?", why: "Signals an actively maintained profile to Google, and shows up directly in search results for customers." },
            { title: "Has the profile been updated recently at all?", why: "A profile left untouched for a long time reads as less trustworthy, both to customers and to Google." },
          ],
        },
      ],
    },
    comparisonTitle: "Your Position in Comparison",
    comparisonDesc: "Businesses that rank high on Google Maps and in AI search (ChatGPT, Perplexity) are often found automatically by customers — with almost no active advertising. Those ranked lower are often not even considered.",
    yourValue: "Your Score",
    gapLabel: "GAP",
    targetBelow: (target) => <>To be found as effortlessly by customers as the most visible businesses in your region, a score of at least{" "}<strong>{target}/100</strong> would be needed.</>,
    targetMet: "Your score is already at the level of the most visible businesses in your region.",
    pillarTableArea: "Area",
    legalAlert: (n, hasImageFlag) => (
      <>
        <strong>{n} legal {n === 1 ? "point" : "points"}</strong>{" "}
        came up during the automatic check
        {hasImageFlag && " — including a note about possibly unlicensed images"}.
      </>
    ),
    details: "Details",
    planEmptyState: "The prioritized 3-month plan is part of the detailed report — request it below.",
    additionalNotesTitle: "Also recommended",
    additionalNotesDesc: "These apply to every business regardless of the specific findings and don't directly affect ranking — mostly measurement or convenience features.",
    comprehensiveGuidanceTitle: "Comprehensive recommendations for your biggest gaps",
    comprehensiveGuidanceDesc: "These recommendations draw on every area we assessed, not just the one the gap was found in.",
    aiVisibilityDesc: (mentioned, total) => <><strong>{mentioned} of {total}</strong>{" "}realistic customer queries mention your business by name.</>,
    aiVisibilityUnavailable: "Test currently unavailable (provider API error) — no result, not \"0% visibility\".",
    claudeTestTitle: "Claude Visibility Test",
    chatgptTestTitle: "ChatGPT Visibility Test",
    geminiTestTitle: "Gemini Visibility Test",
    perplexityTestTitle: "Perplexity Visibility Test",
    googleAiOverviewTitle: "Google AI Overview",
    googleAiModeTitle: "Google AI Mode",
    aiVisibilityGroupTitle: "AI Visibility",
    aiActionSummaryTitle: "How to appear in these AI answers",
    citedShareDesc: (cited, total) => <><strong>{cited} of {total}</strong>{" "}also link to your website as a source.</>,
    showAnswer: "Show the AI's answer",
    hideAnswer: "Hide answer",
    sourceAttributionTitle: "Where the AI gets its information",
    sourceAttributionDesc: "These sources were actually cited by the AI in the answers above — ranked by how often they appeared. Entries marked in orange are directories you're not listed in yet.",
    sourceAttributionCount: (n) => `cited ${n}×`,
    sourceAttributionRegister: "Register now",
    consistencyLabel: "Consistency",
    consistencyRate: (mentioned, total, rate) => `${mentioned} of ${total} attempts (${rate ?? 0}%)`,
    aiVolumeTitle: "Estimated AI demand signal",
    aiVolumeDesc: "How often these questions are estimated to come up in AI searches, derived from Google search data — not real ChatGPT/Gemini usage numbers, an approximation.",
    aiVolumePerMonth: (n) => `${n}/month`,
    competitorAiTitle: "You vs. your strongest competitors in AI answers",
    competitorAiDesc: (n) => `How often each one is named by the ${n} AI answers tested above.`,
    externalDomainsTitle: "Other providers the AI named",
    externalDomainsDesc: "These sites were cited as sources in the AI answers but don't belong to any of the businesses tracked above — likely other competitors outside this search.",
    questionsTitle: "Your Customers' Questions",
    questionsDesc: (seed, covered, total) => (
      <>
        This is what people ask Google around „{seed}". Your website has visible content
        for <strong>{covered} of {total}</strong> of these questions. Every unanswered question
        is a ready-made topic for a page or FAQ entry — and question-and-answer sections like
        that get cited disproportionately often by AI assistants.
      </>
    ),
    localVariantsTitle: "Local Search Variants",
    localVariantsDesc: (covered, total) => (
      <>
        This is how people actually search in your region — usually with a district or add-on
        service. Your website covers <strong>{covered} of {total}</strong> of these. For every
        missing district there is currently no page that could rank there.
      </>
    ),
    questionsSource: "Source: Google autocomplete suggestions. \"Not found\" means we found no matching content on the pages we could read — not necessarily that the question goes unanswered anywhere.",
    mapsTitle: "Your Position on Google Maps",
    mapsDesc: (kw) => <>Live result for the search „{kw}" — this is what you look like on Google Maps right now.</>,
    mapsShortLabel: "Google Maps",
    mapsActionSummaryTitle: "How to improve your visibility on Google Maps",
    organicTitle: "Your Position in Google Search",
    organicShortLabel: "Google Search",
    rankFound: (rank, total) => <>You rank <strong>#{rank}</strong> out of{" "}{total} visible results.</>,
    mapsNotFound: (total) => `You don't appear among the first ${total} results on Google Maps for this search.`,
    organicNotFound: (total) => `You don't appear among the first ${total} organic results on Google for this search.`,
    heatmapTitle: "Visibility Heatmap",
    heatmapDesc: (size, kw) => `We measured where you show up on Google Maps for you — at ${size * size} points around your region, each for the search „${kw}". Green means: you're right at the top. Red means: you don't show up there at all.`,
    technicalGroupTitle: "Technical & Off-Page Signals",
    citationsTitle: "Directory & Social Media Listings",
    citationsDescDetailed: (found, total) => <>Consistent listings on major directory websites ("citations") and active social media profiles are important signals for local Google ranking. You can be found on <strong>{found} of {total}</strong> checked platforms.{" "}This checks whether a listing exists — not whether the contact details on it are correct.</>,
    citationsDescTeaser: (found, total) => <>Consistent listings on major directory websites ("citations") and active social media profiles are important signals for local Google ranking. You can be found on <strong>{found} of {total}</strong> checked platforms.{" "}Which platforms exactly — and where listings are still missing — you'll see in the detailed report.</>,
    directories: "Business Directories",
    socialMedia: "Social Media",
    morePlatforms: "Other Platforms (not automatically checkable)",
    signUpAt: (label) => `Sign up on ${label}`,
    backlinksTitle: "Referring Domains",
    backlinksDesc: (domains, total) => <><strong>{domains}</strong> different websites link to your page ({total} backlinks in total). This is a signal of your website's authority outside of Google — not a visitor counter.</>,
    pagespeedTitle: "Load Time & Technical Quality (Mobile)",
    pagespeedDesc: "Measured with Google PageSpeed Insights — the same technology that also influences Google's ranking.",
    pagespeedMetrics: ["Performance", "SEO", "Accessibility", "Best Practices"],
    lcpLabel: "Largest Contentful Paint (load time of largest element)",
    clsLabel: "Cumulative Layout Shift (movement while loading)",
    analyticsTitle: "Analytics Tools",
    analyticsDesc: "Do you know how many visitors or inquiries your website actually generates?",
    analyticsNone: "No analytics tool (e.g. Google Analytics) was found on your website — you currently have no way of knowing how many visitors your site gets.",
    techSeoTitle: "Technical Findability",
    techSeoDesc: "Can Google even add your homepage to its search index, and are there pages that accidentally duplicate each other?",
    techSeoTeaser: (positive, issues) => (
      <>
        {positive > 0 && <><strong>{positive}</strong>{" "}{positive === 1 ? "confirmation" : "confirmations"}{issues > 0 ? " and " : "."}</>}
        {issues > 0 && <><strong>{issues}</strong>{" "}{issues === 1 ? "improvement point" : "improvement points"}{" "}for Google findability. Details in the detailed report.</>}
      </>
    ),
    adsTrackingTitle: "Ad Success Tracking",
    adsTrackingDesc: "If you run ads on Google or Meta (Facebook/Instagram), this shows whether an inquiry or a purchase actually gets counted as a success.",
    adsTrackingTeaser: (n) => (
      <>The automatic check found {n === 1 ? "1 point" : `${n} points`} about ad success tracking. Details are in the detailed report.</>
    ),
    contentOpportunitiesTitle: "Content Opportunities",
    contentArticlesTitle: "Possible Articles for Your Website",
    quickPostsTitle: "What You Can Share Today",
    missingDirectoriesTitle: "Directories Where Your Competitors Are Listed",
    impactLabel: (level) => ({ high: "High impact", medium: "Medium impact", low: "Low impact" }[level] || level),
    effortLabel: (level) => ({ high: "High effort", medium: "Medium effort", low: "Low effort" }[level] || level),
    missingDirectoryFoundBy: (names) => `Found for: ${names.join(", ")}`,
    napTitle: "NAP Consistency",
    napDesc: "Do your name, address and phone number match across all platforms? Inconsistent data weakens the trust Google places in your location data.",
    napSummary: (consistent, checked, hasDetail) => (
      <><strong>{consistent} of {checked}</strong> checked platforms match completely.{" "}{hasDetail ? "Details on the mismatching listings are below." : "Which platform exactly differs is shown in the detailed report."}</>
    ),
    napStatus: { match: "matches", partial: "partially different — please check", mismatch: "different — please check" },
    legalTitle: "Legal Compliance Points",
    legalTeaser: (n, hasImageFlag) => (
      <>The automatic check found {n === 1 ? "1 point" : `${n} points`} you should have checked{hasImageFlag ? " — including a note about possibly unlicensed images on your website" : ""}. In Germany, these topics in particular frequently lead to formal warnings (Abmahnungen) that can quickly cost several hundred euros. Details are in the detailed report.</>
    ),
    legalChecklistTitle: "Legal checklist for your website",
    legalChecklistIntro: "We examined your website and are sharing a checklist of the legal points a website in Germany should have. What we found is marked green — what's missing or needs checking is red, each with a short note on what to do.",
    legalChecklist: {
      impressum: "Imprint (Impressum)",
      impressumOk: "An imprint was found and contains the key required information.",
      datenschutz: "Privacy policy",
      datenschutzOk: "A privacy policy was found and covers the key points.",
      consent: "Cookie and tracking consent",
      consentOk: "No signs of tracking without prior consent were found.",
      images: "Image rights",
      imagesOk: "No signs of unlicensed images were found.",
      agb: "Terms & withdrawal notice",
      agbOk: "Terms (AGB) were found.",
      ownSite: "Own website",
      other: "Other legal notes",
    },
    legalDisclaimer: (
      <><strong>This is not legal advice.</strong> Only what is technically visible on your website was checked. Whether and to what extent an obligation applies to you depends on your individual case — please have these points reviewed by a lawyer if in doubt.</>
    ),
    linkStructureTitle: "Link Structure",
    linkStructureTeaser: (positive, issues) => (
      <>
        {positive > 0 && <><strong>{positive}</strong> positive{" "}{positive === 1 ? "confirmation" : "confirmations"}{issues > 0 ? " and " : "."}</>}
        {issues > 0 && <><strong>{issues}</strong>{" "}{issues === 1 ? "improvement point" : "improvement points"}{" "}for internal linking (main menu, internal links). Details in the detailed report.</>}
      </>
    ),
    linkStructureDesc: "Is every important page reachable from the main menu, and do the internal links on your website work?",
    reviewsTitle: "Google Reviews",
    reviewsLabel: "Reviews",
    reviewQualityLabel: "Review Quality",
    averageRatingLabel: "Average",
    newIn90Days: "new in 90 days",
    newestReview: "Newest review",
    showcaseQualityLabel: "Showcase Review Quality",
    showcaseQualityNote: "How substantive the reviews Google shows by default are (location, service, detail) — not a star average, hence a 100-point rather than 5-point scale.",
    reviewsTeaser: (count, median, showcaseIndex) => (
      <>
        You have <strong>{count} reviews</strong>
        {median != null && <> — the median for comparable businesses in your region is <strong>{median}</strong></>}.
        But ranking isn't only about the count: how recent your reviews are, whether you reply
        to them, and how specific they are all play a part.
        {showcaseIndex != null && <> The reviews Google highlights for you score <strong>{showcaseIndex}/100</strong> on that.</>}
      </>
    ),
    reviewsDeliverablesLead: "The detailed report shows you:",
    reviewsDeliverables: [
      "Up to four themes your customers praise most — and up to four they criticize most",
      "A ready-to-post reply draft for every critical review — or, where it violates Google's policies, the direct path to reporting it to Google",
      "How the content of new reviews can support your visibility on Google Maps",
    ],
    reviewTipsTitle: "Review Management Tips",
    reviewTipsRequestTitle: "Templates for Requesting Reviews",
    reviewTipsWhatsapp: "WhatsApp / SMS:",
    reviewTipsWhatsappText: (name) => `"Hi! If you were happy with ${name}, we'd really appreciate a quick Google review — it only takes a minute. [Google review link]"`,
    reviewTipsEmail: "Email:",
    reviewTipsEmailText: "\"Thank you for working with us. Your experience matters to us. Could we ask you for a quick Google review? It only takes a minute. [Google review link]\"",
    reviewTipsNegativeTitle: "Responding to Negative Reviews (3 Steps)",
    reviewTipsNegativeStep1: "Thank them:",
    reviewTipsNegativeStep1Text: "\"Thank you for your feedback. Customer satisfaction matters a lot to us.\"",
    reviewTipsNegativeStep2: "Show empathy:",
    reviewTipsNegativeStep2Text: "\"We're truly sorry to hear that — this isn't the experience we aim for.\"",
    reviewTipsNegativeStep3: "Offer a resolution:",
    reviewTipsNegativeStep3Text: "\"We'd like to sort this out with you directly. Please reach us at [phone/email] so we can make it right.\"",
    reviewTipsFooter: "Tip: respond to negative reviews within 24 hours — a fast, professional reply builds trust with other customers.",
    showcaseTitle: "Your Most Visible Google Reviews",
    showcaseDesc: "By default, Google shows users only a small selection of the best reviews. That exact selection is what you see here — it's your digital showcase.",
    noSpecificDetails: "no specific details",
    strengthLabel: (q) => `Strength ${q}/5`,
    strengthTooltip: "How many locally-relevant SEO details this review contains (location, brand, service, detail)",
    reputationTitle: "Online Reputation in Detail",
    reputationDesc: (sample, total) => <>Analysis of {sample} of {total} total{" "}Google reviews — not just the 5 Google shows by default.</>,
    responseRate: "Response rate",
    negativeReviews: "Negative reviews",
    responseQualityIntro: "Responses to reviews come across as",
    responseQuality: { individuell: "individually addressing each case", standardisiert: "like a standard template", none: "absent — not a single response was found" },
    positiveThemes: "What customers are happy about",
    negativeThemes: "What customers complain about",
    appealableTitle: "Reviews you could report to Google",
    appealableDesc: "These reviews show signs that may violate Google's own content policy for reviews. This is not a removal guarantee — Google decides on each report individually.",
    appealableCategory: {
      off_topic: "Not about their own visit",
      spam_fake: "No genuine visit apparent",
      conflict_of_interest: "Possible conflict of interest (e.g. a competitor)",
      harassment_hate: "Personal attack rather than feedback",
      restricted_content: "Prohibited content",
    },
    appealableViewOnGoogle: "View & report on Google",
    appealableNoneFound: (n) => `We checked your ${n} negative reviews with text against Google's review content policy — none of them show signs that would make a report to Google promising.`,
    respondableTitle: "Suggested reply for these reviews",
    respondableDesc: "These reviews don't violate Google's policy — here's a ready-made reply you can post publicly under the review on Google.",
    respondableCopy: "Copy",
    respondableCopied: "Copied to clipboard",
    respondableViewOnGoogle: "View on Google",
    competitorTableTitle: "Compared to Your Strongest Competitors",
    company: "Business",
    rating: "Rating",
    lastReview: "Last review",
    topNegativeTheme: "Most common negative theme",
    youSuffix: (name) => `${name} (You)`,
    thankYou: "Thank you — we'll be in touch.",
    ctaTitleDetailed: "Questions about this report?",
    ctaDesc: "No sales pitch — if you'd like to know more, feel free to reach out here. No obligation at all.",
    formName: "Your name (optional)",
    formContact: "Phone or email",
    formMessage: "Your message (optional)",
    sendMessage: "Send message",
    requestDetailed: "Request the Detailed Report",
    trustedBy: "Trusted by businesses like yours",
    imgConfirmed: "Confirmed:",
    imgFoundElsewhere: (n) => `This image was also found on ${n} other, independent sites —`,
    imgMore: (n) => `+${n} more`,
    imgNoMatches: "No matches found on other sites.",
    back: "Back",
    next: "Next",
    imagesCount: (n) => `${n} images`,
    gbpPhotosTitle: "Photos in Your Google Business Profile",
    gbpPhotosDesc: "We also checked the photos you uploaded yourself to your Google Business Profile with reverse image search — not just the ones on your website.",
    gbpImagesCleanTitle: "Google Business Profile images checked",
    gbpImagesCleanDesc: (n) => `We checked ${n} photos you uploaded yourself to your Google Business Profile with reverse image search — none of them turned up on another, unrelated site.`,
  },
};
